import { 
    Company, 
    Proposal, 
    People, 
    Surrogate, 
    Product, 
    Process, 
    ProfitRisk, 
    UseCase, 
    Solution,
    User
  } from '../types';
  
  // Mock Users
  export const users: User[] = [
    {
      id: '1',
      name: 'Jane Smith',
      role: 'owner',
      company: 'TechInnovate'
    },
    {
      id: '2',
      name: 'John Doe',
      role: 'surrogate',
    }
  ];
  
  // Mock Companies
  export const companies: Company[] = [
    {
      id: '1',
      name: 'TechInnovate',
      description: 'A leading technology innovation company focused on AI solutions',
      vision: 'To transform industries through intelligent automation',
      mission: 'Deliver cutting-edge AI solutions that solve real-world problems',
      objectives: [
        {
          id: '1',
          title: 'Market Expansion',
          description: 'Expand into new markets and industries',
          keyResults: [
            {
              id: '1',
              description: 'Enter 3 new industry verticals',
              target: 3,
              current: 1
            },
            {
              id: '2',
              description: 'Achieve 25% revenue growth',
              target: 25,
              current: 15
            }
          ]
        }
      ]
    }
  ];
  
  // Mock Proposals
  export const proposals: Proposal[] = [
    {
      id: '1',
      title: 'AI-Powered Customer Service Platform',
      description: 'Develop an intelligent customer service platform that uses AI to handle routine inquiries and escalate complex issues to human agents.',
      companyId: '1',
      ownerId: '1',
      timelineType: 'initiative',
      estimatedTimeline: '1 year',
      createdAt: new Date('2025-01-15'),
      status: 'submitted'
    },
    {
      id: '2',
      title: 'Predictive Maintenance System',
      description: 'Create a system that predicts equipment failures before they occur using machine learning and IoT sensors.',
      companyId: '1',
      ownerId: '1',
      timelineType: 'epic',
      estimatedTimeline: '3 months',
      createdAt: new Date('2025-02-01'),
      status: 'in-review'
    }
  ];
  
  // Mock People
  export const people: People[] = [
    {
      id: '1',
      role: 'Customer Support Agent',
      needs: ['Efficient tools to handle customer inquiries', 'Clear escalation paths'],
      wants: ['Reduced repetitive tasks', 'Better customer satisfaction metrics'],
      painPoints: ['Overwhelmed by volume of routine questions', 'Difficulty tracking customer history']
    },
    {
      id: '2',
      role: 'Customer',
      needs: ['Quick resolution to problems', 'Available support 24/7'],
      wants: ['Personalized service', 'No need to repeat information'],
      painPoints: ['Long wait times', 'Having to explain issues multiple times']
    }
  ];
  
  // Mock Surrogates
  export const surrogates: Surrogate[] = [
    {
      id: '1',
      name: 'AI Assistant Model',
      type: 'ai',
      coreSkills: ['Natural Language Processing', 'Intent Recognition', 'Sentiment Analysis'],
      domainExpertise: ['Customer Service', 'FAQ Handling'],
      automationCapability: 9,
      efficiency: 8
    },
    {
      id: '2',
      name: 'Sarah Johnson',
      type: 'human',
      coreSkills: ['Python Development', 'Machine Learning', 'System Architecture'],
      domainExpertise: ['Customer Service Systems', 'AI Integration'],
      automationCapability: 7,
      efficiency: 9
    }
  ];
  
  // Mock Products
  export const products: Product[] = [
    {
      id: '1',
      name: 'Legacy Customer Support System',
      description: 'Current ticket-based customer support system with limited automation',
      type: 'existing'
    },
    {
      id: '2',
      name: 'AI-Powered Customer Service Platform',
      description: 'Intelligent platform that handles routine inquiries automatically and provides agents with AI assistance for complex issues',
      type: 'solution',
      purpose: 'Improve customer satisfaction while reducing support costs',
      scale: {
        volume: 8,
        velocity: 9,
        variety: 7,
        variability: 6,
        value: 9
      },
      trustMetrics: {
        successCriteria: [
          'Reduce average response time by 50%',
          'Maintain customer satisfaction above 90%',
          'Automate handling of at least 60% of inquiries'
        ],
        definitionOfDone: [
          'System handles all common customer inquiries without human intervention',
          'Integration with existing CRM complete',
          'Agent dashboard provides real-time AI suggestions'
        ]
      }
    }
  ];
  
  // Mock Processes
  export const processes: Process[] = [
    {
      id: '1',
      name: 'Current Customer Support Process',
      description: 'Manual ticket creation and routing process with human agents handling all inquiries',
      steps: [
        'Customer submits inquiry through web form or phone',
        'Support ticket is created manually',
        'Ticket is assigned to available agent',
        'Agent researches and responds to customer',
        'Ticket is closed upon resolution'
      ],
      type: 'current'
    },
    {
      id: '2',
      name: 'Future AI-Powered Support Process',
      description: 'Automated initial response with AI handling routine inquiries and routing complex issues',
      steps: [
        'Customer submits inquiry through any channel',
        'AI analyzes inquiry and provides immediate response for routine questions',
        'Complex issues are routed to appropriate human agent with AI-generated context',
        'Agent resolves issue with AI assistance',
        'System learns from resolution for future improvements'
      ],
      type: 'future'
    }
  ];
  
  // Mock Profit/Risk
  export const profitRisks: ProfitRisk[] = [
    {
      id: '1',
      description: 'High customer support costs due to large support team',
      financialImpact: -500000,
      type: 'risk',
      timeline: 'before'
    },
    {
      id: '2',
      description: 'Customer churn due to slow response times',
      financialImpact: -750000,
      type: 'risk',
      timeline: 'before'
    },
    {
      id: '3',
      description: 'Reduced support costs through automation',
      financialImpact: 400000,
      type: 'profit',
      timeline: 'after'
    },
    {
      id: '4',
      description: 'Increased customer retention through improved satisfaction',
      financialImpact: 900000,
      type: 'profit',
      timeline: 'after'
    }
  ];
  
  // Mock Use Cases
  export const useCases: UseCase[] = [
    {
      id: '1',
      title: 'Password Reset Assistance',
      description: 'Customer needs help resetting their account password',
      timeline: 'current'
    },
    {
      id: '2',
      title: 'Product Feature Inquiries',
      description: 'Customer has questions about specific product features',
      timeline: 'current'
    },
    {
      id: '3',
      title: 'Proactive Service Notifications',
      description: 'System detects potential issues and notifies customers before they experience problems',
      timeline: 'future'
    },
    {
      id: '4',
      title: 'Personalized Product Recommendations',
      description: 'AI analyzes customer history and provides tailored product suggestions during support interactions',
      timeline: 'future'
    }
  ];
  
  // Mock Solutions
  export const solutions: Solution[] = [
    {
      id: '1',
      proposalId: '1',
      title: 'Integrated AI Customer Service Platform',
      description: 'A comprehensive platform that combines NLP, machine learning, and a user-friendly interface to transform customer service operations.',
      implementationPlan: 'Phase 1: Develop AI models for common inquiries\nPhase 2: Create agent dashboard with AI assistance\nPhase 3: Integrate with existing systems\nPhase 4: Deploy and train staff',
      recommendedSurrogates: [
        {
          id: '1',
          name: 'AI Assistant Model',
          type: 'ai',
          coreSkills: ['Natural Language Processing', 'Intent Recognition', 'Sentiment Analysis'],
          domainExpertise: ['Customer Service', 'FAQ Handling'],
          automationCapability: 9,
          efficiency: 8
        },
        {
          id: '2',
          name: 'Sarah Johnson',
          type: 'human',
          coreSkills: ['Python Development', 'Machine Learning', 'System Architecture'],
          domainExpertise: ['Customer Service Systems', 'AI Integration'],
          automationCapability: 7,
          efficiency: 9
        }
      ],
      estimatedCost: 250000,
      estimatedTimeframe: '9 months',
      status: 'proposed'
    }
  ];